import { useState, useEffect, useRef, useContext } from "react";
import Card from "./Card";
import styles from "./style.module.css";
import { useActiveCriteria } from "../Filter/GrantFilterProvider";
import { useAuth } from "../AuthContext";
import { selectedHitContext } from "../GrantView/SelectedHitProvider";
import { filterDataContext } from "../Filter/FilterDataProvider";
import { read, write, useMemoryKey } from "../utilities/pageMemory";

// Step 10: the backend holds all grants and answers with 6 matching cards at
// a time. Filters, search, the deadline sort, and the four mark views all
// run over ALL grants on the backend — never just the loaded ones.
export default function GrantList({ activeToggle, onCounts }) {
  const [grants, setGrants] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [sortBySoonest, setSortBySoonest] = useState(false);

  const criteria = useActiveCriteria();
  const { setAvailablePlaces } = useContext(filterDataContext);
  const { user } = useAuth();
  const containerRef = useRef(null);
  // Step 31: the remembered grant and the remembered scroll position.
  const selected = useContext(selectedHitContext);
  const scrollKey = useMemoryKey("scroll");
  const scrollRestore = useRef({ done: false, target: null, pagesLoaded: 0 });
  const scrollWriteTimer = useRef(null);
  // the latest fetchGrants, for the restore effect below
  const fetchMore = useRef(null);

  const requestBody = (start) => ({
    pageSize: 6,
    start: start || 0,
    search: criteria.searchText || "",
    categories: criteria.byCategory["Categories"] || [],
    agencies: criteria.byCategory["Agency Type"] || [],
    // Step 34: the three page words Non-Profit / For Profit / B Corp; the
    // program matches them against the record's "Applicant Types".
    businessTypes: criteria.byCategory["Business Type"] || [],
    states: criteria.byCategory["Geographic Focus"] || [],
    // Step 7: State is its own filter now, sent separately from the places.
    deadlineAfter: criteria.deadlineAfter || "",
    amountMin: criteria.amountMin,
    amountMax: criteria.amountMax,
    sort: sortBySoonest ? "soonest" : "",
    view: activeToggle ? activeToggle.toLowerCase() : "",
  });

  async function fetchGrants(start) {
    try {
      if (start) setLoadingMore(true);
      else setLoading(true);

      const baseurl = process.env.REACT_APP_API_URL;
      // Signed in: send a fresh login pass so the answer carries this
      // user's own marks (a stored pass expires after an hour).
      const headers = { "Content-Type": "application/json" };
      if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
      const res = await fetch(`${baseurl}/grantLists`, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody(start)),
      });
      if (!res.ok) throw new Error("HTTP error! status: " + res.status);

      const data = await res.json();
      if (data.places && setAvailablePlaces) setAvailablePlaces(data.places);
      setGrants((prev) => (start ? [...prev, ...(data.docs || [])]
                                 : data.docs || []));
      setNextCursor(data.nextCursor ?? null);
      setCounts({
        totalMatching: data.totalMatching,
        noInfoCount: data.noInfoCount,
        totalGrants: data.totalGrants,
      });
    } catch (err) {
      console.error("Error fetching grants:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  fetchMore.current = fetchGrants;

  // A fresh first page whenever the filters, search, sort, view, or the
  // signed-in user change (marks belong to the user, so the answer does too).
  const criteriaKey = JSON.stringify(requestBody(0));
  useEffect(() => {
    setGrants([]);
    setNextCursor(null);
    fetchGrants(0);
  }, [criteriaKey, user]);

  // Step 29: the count line is drawn above the search box by the Results
  // column, so the numbers are handed up whenever they change.
  useEffect(() => {
    if (typeof onCounts === "function") onCounts(counts);
  }, [counts, onCounts]);

  // Step 31: re-select the remembered grant from the cards just loaded; if
  // it is not among the first page, the provider asks the list route once.
  useEffect(() => {
    if (!selected || !selected.pendingGrantId || loading) return;
    if (typeof selected.offerGrants === "function" && selected.offerGrants(grants)) return;
    if (typeof selected.lookUpPending === "function") selected.lookUpPending();
  }, [grants, loading, selected]);

  // Step 31: how far the list was scrolled is remembered (written a moment
  // after each scroll) and put back once the first page has drawn; if the
  // saved spot is further than what is loaded, up to three more pages are
  // fetched, then it gives up quietly.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    function rememberScroll() {
      if (!scrollRestore.current.done) return;
      if (scrollWriteTimer.current) clearTimeout(scrollWriteTimer.current);
      scrollWriteTimer.current = setTimeout(() => {
        write(scrollKey, Math.round(container.scrollTop));
      }, 150);
    }
    container.addEventListener("scroll", rememberScroll);
    return () => {
      container.removeEventListener("scroll", rememberScroll);
      if (scrollWriteTimer.current) clearTimeout(scrollWriteTimer.current);
    };
  }, [scrollKey, grants.length]);

  useEffect(() => {
    const state = scrollRestore.current;
    if (state.done || loading || grants.length === 0) return;
    const container = containerRef.current;
    if (!container) return;
    if (state.target == null) {
      const saved = Number(read(scrollKey));
      state.target = Number.isFinite(saved) && saved > 0 ? saved : 0;
    }
    if (state.target <= 0) { state.done = true; return; }
    container.scrollTop = state.target;
    const reached = container.scrollTop >= state.target - 2;
    if (reached || nextCursor == null || loadingMore || state.pagesLoaded >= 3) {
      state.done = true;
      return;
    }
    state.pagesLoaded += 1;
    if (typeof fetchMore.current === "function") fetchMore.current(nextCursor);
  }, [grants, loading, loadingMore, nextCursor, scrollKey]);

  // More cards as the list is scrolled.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function handleScroll() {
      if (nextCursor == null || loadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollTop + clientHeight >= scrollHeight - 50) {
        fetchGrants(nextCursor);
      }
    }

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [nextCursor, loadingMore, criteriaKey]);

  // The wider two-column design can make the first six cards shorter than
  // the viewport. In that case there is nothing to scroll, so a scroll-only
  // loader can never request the next page. Keep fetching one six-card page
  // at a time until the list genuinely overflows (or the backend says there
  // are no more pages). ResizeObserver repeats the same check if the window
  // or surrounding layout changes size.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || nextCursor == null || loading || loadingMore) return undefined;

    let frame = 0;
    const loadIfListDoesNotScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (
          container.clientHeight > 0 &&
          container.scrollHeight <= container.clientHeight + 1 &&
          typeof fetchMore.current === "function"
        ) {
          fetchMore.current(nextCursor);
        }
      });
    };

    loadIfListDoesNotScroll();
    const observer = new ResizeObserver(loadIfListDoesNotScroll);
    observer.observe(container);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [grants.length, nextCursor, loading, loadingMore, criteriaKey]);

  // A mark click updates what this page already shows: hiding a grant takes
  // it out of the main list at once, and unmarking a grant inside a mark
  // view (for example unhiding in the Hidden view) takes it out of that view
  // — which is the unhide round trip: it is back in the main list.
  const onMarkChange = (grantId, mark, value) => {
    setGrants((prev) => {
      const updated = prev.map((g) =>
        g["GrantWatch ID#"] === grantId
          ? { ...g, [{ doing: "Doing", liked: "Liked", viewed: "Viewed",
                       hidden: "Hidden" }[mark]]: value }
          : g);
      if (mark === "hidden" && value && !activeToggle) {
        return updated.filter((g) => g["GrantWatch ID#"] !== grantId);
      }
      if (activeToggle && mark === activeToggle.toLowerCase() && !value) {
        return updated.filter((g) => g["GrantWatch ID#"] !== grantId);
      }
      return updated;
    });
  };

  if (loading && grants.length === 0) {
    return <p className={styles.cardsNote}>Loading grants...</p>;
  }

  if (!grants.length) {
    if (activeToggle) {
      return (
        <p className={styles.cardsNote}>
          You haven't marked any grants as {activeToggle} yet.
        </p>
      );
    }
    return <p className={styles.cardsNote}>No grants match the current filters.</p>;
  }

  // Step 29: the sort button is not in the owner's picture. The state and
  // the "sort" request field stay; restore the button by uncommenting this.
  // <button type="button" onClick={() => setSortBySoonest((on) => !on)}>
  //   {sortBySoonest ? "✓ " : ""}Sort by soonest deadline
  // </button>
  // The count line ("24 of 1,284 grants") is drawn by the Results column
  // from the numbers handed up above.
  void setSortBySoonest;

  return (
    <>
      <div ref={containerRef} className={styles.cardsList}>
        {grants.map((grant) => (
          <Card key={grant["GrantWatch ID#"]} hit={grant}
                onMarkChange={onMarkChange} />
        ))}

        {loadingMore && <p className={styles.cardsMore}>Loading more...</p>}
      </div>
    </>
  );
}
